// Persists the last successful /api/env response so the app can boot offline
// (tenant id, login URL, feature flags) without any network round-trip.
// Single source of truth for "which tenant does this browser belong to" —
// every offline cache / IndexedDB name is derived from `TENANT` stored here.

export const LAST_ENV_KEY = 'od.lastEnv';

function storageOrNull(storage) {
  if (storage) return storage;
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

export function loadLastEnv(storage) {
  const s = storageOrNull(storage);
  if (!s) return null;
  try {
    const raw = s.getItem(LAST_ENV_KEY);
    if (!raw) return null;
    const env = JSON.parse(raw);
    if (!env || typeof env !== 'object' || typeof env.TENANT !== 'string' || !env.TENANT) return null;
    return env;
  } catch {
    return null;
  }
}

export function saveLastEnv(env, storage) {
  const s = storageOrNull(storage);
  if (!s || !env || typeof env !== 'object') return false;
  try {
    s.setItem(LAST_ENV_KEY, JSON.stringify(env));
    return true;
  } catch {
    return false;
  }
}

export function getRememberedTenant(storage) {
  return loadLastEnv(storage)?.TENANT || null;
}

// GET /api/env with a hard timeout. Rejects on network error, timeout or
// non-2xx so the caller can fall back to the remembered env.
export async function fetchEnvWithTimeout(timeoutMs = 4000, fetchImpl = globalThis.fetch) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetchImpl('/api/env', { signal: ctrl.signal, cache: 'no-store' });
    if (!res.ok) throw new Error(`env ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}
